import { createAction } from '@reduxjs/toolkit';
import {
  BroadcastWSMessage,
  ChatWSMessage,
  MeetingWSMessage,
  RoomWSMessage,
  isBroadcastWSMessage,
  isChatWSMessage,
  isMeetingWSMessage,
  isRoomWSMessage,
} from 'common/constants';
import { SocketRoomPrefix } from 'modules/webSocket/state/types';

type Message = MeetingWSMessage | ChatWSMessage | BroadcastWSMessage | RoomWSMessage;

type MeetingWSMessageAction<M extends Message> = `${typeof SocketRoomPrefix.Meetings}:${M}`;

/**
 * Factory function for creating WebSocket message actions related to **Meeting***.
 *
 * @template P - The payload type of the WebSocket message action (default is undefined).
 *
 * @param {Message} message - The specific action or event identifier to create a WebSocket message for.
 *
 * @returns {string} A WebSocket message action identifier generated by concatenating the 'Meetings' namespace
 *                 with the provided action string.
 */
export const createMeetingWSMessage = <P = undefined>(message: Message) => {
  if (
    !(
      isMeetingWSMessage(message) ||
      isChatWSMessage(message) ||
      isRoomWSMessage(message) ||
      isBroadcastWSMessage(message)
    )
  )
    throw Error(`Message '${message}' is not supported by Meeting WS`);

  return createAction<P, MeetingWSMessageAction<typeof message>>(`${SocketRoomPrefix.Meetings}:${message}`);
};
